import React, { useState, useEffect } from 'react';
import type { MenuProps } from 'antd';
import { Menu, Button } from 'antd';
import type { SelectInfo } from 'rc-menu/lib/interface';
import { MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons';
import { UserInfo } from '@/models/perms_type';
import styles from './index.module.less';

type MenuItem = Required<MenuProps>['items'][number];

interface ProductNavMenuProps {
    userInfo: UserInfo;
    collapsed: boolean;
    productNavMenu: MenuItem[];
    onCollapse: (collapsed: boolean) => void;
    handleJumpTo: (path: string) => void;
    activeMenu: string;
    pathname: string;
}

const ProductNavMenu: React.FC<ProductNavMenuProps> = ({
    userInfo,
    collapsed,
    onCollapse,
    handleJumpTo,
    activeMenu,
    productNavMenu,
    pathname
}) => {
    const [openKeys, setOpenKeys] = useState<string[]>([]);
    const [selectedKeys, setSelectedKeys] = useState<string[]>([]);

    useEffect(() => {
        // 查找包含当前路径的父菜单（支持二级和三级菜单）
        let matchedParent: MenuItem | undefined;
        let matchedGrandParent: MenuItem | undefined;

        for (const item of productNavMenu) {
            if (item && 'children' in item && item.children) {
                // 检查二级菜单
                const childMatch = item.children.some(child => child && child.key === pathname);
                if (childMatch) {
                    matchedParent = item;
                    break;
                }

                // 检查三级菜单
                for (const child of item.children) {
                    if (child && 'children' in child && Array.isArray(child.children)) {
                        const grandChildMatch = child.children.some(grandChild => grandChild && grandChild.key === pathname);
                        if (grandChildMatch) {
                            matchedParent = item;
                            matchedGrandParent = child;
                            break;
                        }
                    }
                }

                if (matchedParent) break;
            }
        }

        // 查找直接匹配的父菜单（父路径匹配）
        if (!matchedParent) {
            matchedParent = productNavMenu.find(item => {
                if (item && 'children' in item) {
                    const itemKey = item.key as string;
                    return pathname === itemKey || pathname === `/taxi${itemKey}`;
                }
                return false;
            });
        }

        if (matchedParent && matchedParent.key) {
            const parentKey = matchedParent.key as string;

            // 如果是三级菜单，需要同时打开一级和二级菜单
            if (matchedGrandParent && matchedGrandParent.key) {
                setOpenKeys([parentKey, matchedGrandParent.key as string]);
            } else {
                setOpenKeys([parentKey]);
            }

            setSelectedKeys([pathname]);
        } else {
            setOpenKeys([]);
            setSelectedKeys([]);
        }
    }, [pathname, productNavMenu]);

    const onOpenChange: MenuProps['onOpenChange'] = (keys) => {
        setOpenKeys(keys);
    };

    const handleCollapse = () => {
        onCollapse(!collapsed);
    };

    const handleMenuSelect = (info: SelectInfo) => {
        // 如果点击的是有三级子菜单的父菜单，自动跳转到第一个子菜单
        for (const item of productNavMenu) {
            if (item && 'children' in item && Array.isArray(item.children)) {
                for (const child of item.children) {
                    if (child && child.key === info.key && 'children' in child && Array.isArray(child.children) && child.children.length > 0) {
                        const firstGrandChild = child.children[0];
                        if (firstGrandChild && 'key' in firstGrandChild) {
                            handleJumpTo(firstGrandChild.key as string);
                            return;
                        }
                    }
                }
            }
        }

        handleJumpTo(info.key);
    };

    return (
        <div className={`${styles.navMenu} ${collapsed ? styles.navMenuCollapsed : ''}`}>
            <Menu
                inlineCollapsed={collapsed}
                mode="inline"
                items={productNavMenu}
                onSelect={handleMenuSelect}
                className={styles.menu}
                selectedKeys={selectedKeys}
                openKeys={openKeys}
                onOpenChange={onOpenChange}
                
            />
            <div className={styles.divider}></div>
            <div className={styles.collapseButton}>
                <Button
                    type="text"
                    icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                    onClick={handleCollapse}
                    className={styles.collapseBtn}
                />
            </div>
        </div>
    );
};

export default ProductNavMenu;
